// Shared flat <-> structured MissionPolicy mapping for the editable policy form.
// Used by SessionMissionDialog, MissionTemplateForm, MissionConfigPage, and agent config.

import {
  COMPLETION_MODE_LABELS,
  resolveMissionTemplatePolicy,
} from "../../agentConfig/tabs/mission/missionTemplateDefaults";

export const STOP_KIND_OPTIONS = [
  { value: "OBJECTIVE_MET", label: "Objective met (with safety caps)" },
  { value: "MANUAL", label: "Manual stop only" },
  { value: "MAX_TURNS", label: "Maximum turns" },
  { value: "MAX_DURATION", label: "Maximum duration" },
];

export const COMPLETION_MODE_OPTIONS = Object.entries(COMPLETION_MODE_LABELS).map(
  ([value, label]) => ({ value, label })
);

export function intOrNull(value) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function longOrNull(value) {
  if (value === "" || value == null) return null;
  const parsed = parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function trimOrNull(value) {
  const trimmed = value != null ? String(value).trim() : "";
  return trimmed || null;
}

export function parseDaysOfWeek(value) {
  if (!value || !String(value).trim()) return [];
  return String(value)
    .split(",")
    .map((part) => parseInt(part.trim(), 10))
    .filter((day) => Number.isFinite(day) && day >= 1 && day <= 7);
}

export function formatDaysOfWeek(days) {
  const list = Array.isArray(days) ? days : [];
  return list.length ? list.join(", ") : "";
}

/** One-shot timed wakes; legacy customDeadlines (date-only) merge in with empty message. */
export function scheduledWakesFromPolicy(scheduledWakes, customDeadlines) {
  const rows = (scheduledWakes || []).map((wake) => ({
    at: wake?.at || "",
    message: wake?.message || "",
  }));
  const seen = new Set(rows.map((row) => row.at).filter(Boolean));
  for (const at of customDeadlines || []) {
    const normalized = String(at || "").trim();
    if (normalized && !seen.has(normalized)) {
      rows.push({ at: normalized, message: "" });
      seen.add(normalized);
    }
  }
  return rows;
}

// ponytail: hand-mirrored twin of MissionPolicyMerger.java (recurse into maps, replace
// lists/scalars wholesale, skip null overrides). Parity is locked by the shared case matrix in
// missionPolicyDraft.test.js / MissionPolicyMergerTest.java. Upgrade path: a single cross-language
// JSON fixture if a shared test-resources harness is ever added.
/** Deep-merge override onto base (mirrors MissionPolicyMerger.java). */
export function mergeMissionPolicy(base, override) {
  const result = deepCopyPolicyMap(base || {});
  if (!override || typeof override !== "object") {
    return result;
  }
  applyPolicyOverride(result, override);
  return result;
}

function deepCopyPolicyMap(source) {
  if (!source || typeof source !== "object") return {};
  const copy = {};
  for (const [key, value] of Object.entries(source)) {
    copy[key] =
      value && typeof value === "object" && !Array.isArray(value)
        ? deepCopyPolicyMap(value)
        : value;
  }
  return copy;
}

function applyPolicyOverride(target, override) {
  for (const [key, overrideValue] of Object.entries(override)) {
    if (overrideValue == null) continue;
    const baseValue = target[key];
    if (
      baseValue &&
      typeof baseValue === "object" &&
      !Array.isArray(baseValue) &&
      overrideValue &&
      typeof overrideValue === "object" &&
      !Array.isArray(overrideValue)
    ) {
      target[key] = deepCopyPolicyMap(baseValue);
      applyPolicyOverride(target[key], overrideValue);
    } else {
      target[key] = overrideValue;
    }
  }
}

/**
 * Flattens a mission policy for forms, filling gaps from the built-in harness template when the
 * stored snapshot is missing or partial (common before orchestration starts).
 */
export function policyDraftFromEffectivePolicy(
  effectivePolicy,
  templateId = "standard_agent"
) {
  const merged = mergeMissionPolicy(
    resolveMissionTemplatePolicy(templateId),
    effectivePolicy || {}
  );
  return policyDraftFromPolicy(merged);
}

/** Flattens a structured MissionPolicy (or built-in default) into the editable draft fields. */
export function policyDraftFromPolicy(policy) {
  const wake = policy?.wakePolicy || {};
  const heartbeat = wake.heartbeat || {};
  const until = policy?.stopPolicy?.until || {};
  const turn = policy?.turnPolicy || {};
  return {
    heartbeatEnabled: heartbeat.enabled === true,
    cadence: heartbeat.cadence || "PT24H",
    heartbeatTimezone: heartbeat.timezone || "",
    heartbeatTimeOfDay: heartbeat.timeOfDay || "",
    heartbeatDaysOfWeek: formatDaysOfWeek(heartbeat.daysOfWeek),
    message: heartbeat.message || "",
    scheduledWakes: scheduledWakesFromPolicy(
      wake.scheduledWakes,
      wake.customDeadlines
    ),
    noProgressEnabled: Boolean(wake.noProgressDeadline),
    noProgressDeadline: wake.noProgressDeadline || "",
    debounceMs: wake.debounceMs != null ? String(wake.debounceMs) : "",
    cooldownMs: wake.cooldownMs != null ? String(wake.cooldownMs) : "",
    maxTurnsPerHour:
      wake.maxTurnsPerHour != null ? String(wake.maxTurnsPerHour) : "",
    skipIfSubjectRunning: wake.skipIfSubjectRunning !== false,
    stopKind: until.kind || "OBJECTIVE_MET",
    maxTurns: until.maxTurns != null ? String(until.maxTurns) : "",
    maxDuration: until.maxDuration || "P30D",
    completionMode: policy?.stopPolicy?.completionMode || "AUTO",
    maxIterations:
      turn.maxIterations != null ? String(turn.maxIterations) : "",
    wakeMessage: turn.wakeMessage || "",
    allowAgentReschedule: turn.allowAgentReschedule === true,
    postCheckToTranscript: turn.postCheckToTranscript === true,
  };
}

function buildHeartbeat(draft) {
  const heartbeat = {
    enabled: draft.heartbeatEnabled,
    cadence: draft.heartbeatEnabled ? draft.cadence : undefined,
    message: trimOrNull(draft.message) || undefined,
  };
  const timezone = trimOrNull(draft.heartbeatTimezone);
  if (timezone) heartbeat.timezone = timezone;
  const timeOfDay = trimOrNull(draft.heartbeatTimeOfDay);
  if (timeOfDay) heartbeat.timeOfDay = timeOfDay;
  const daysOfWeek = parseDaysOfWeek(draft.heartbeatDaysOfWeek);
  if (daysOfWeek.length > 0) heartbeat.daysOfWeek = daysOfWeek;
  return heartbeat;
}

function buildScheduledWakes(draft) {
  return (draft.scheduledWakes || [])
    .map((wake) => ({
      at: trimOrNull(wake?.at),
      message: trimOrNull(wake?.message),
    }))
    .filter((wake) => wake.at)
    .map((wake) => ({
      at: wake.at,
      ...(wake.message ? { message: wake.message } : {}),
    }));
}

/** Rebuilds a structured MissionPolicy from the editable draft fields. */
export function policyFromDraft(draft) {
  const wakePolicy = {
    heartbeat: buildHeartbeat(draft),
    scheduledWakes: buildScheduledWakes(draft),
    eventWakes: [],
    customDeadlines: [],
    skipIfSubjectRunning: draft.skipIfSubjectRunning !== false,
  };

  const maxTurnsPerHour = intOrNull(draft.maxTurnsPerHour);
  if (maxTurnsPerHour != null) {
    wakePolicy.maxTurnsPerHour = maxTurnsPerHour;
  }

  if (draft.noProgressEnabled) {
    const noProgress = trimOrNull(draft.noProgressDeadline);
    if (noProgress) wakePolicy.noProgressDeadline = noProgress;
  }

  const debounceMs = longOrNull(draft.debounceMs);
  if (debounceMs != null) wakePolicy.debounceMs = debounceMs;

  const cooldownMs = longOrNull(draft.cooldownMs);
  if (cooldownMs != null) wakePolicy.cooldownMs = cooldownMs;

  const wakeMessage = trimOrNull(draft.wakeMessage);

  return {
    wakePolicy,
    stopPolicy: {
      until: {
        kind: draft.stopKind,
        maxTurns: intOrNull(draft.maxTurns),
        maxDuration: draft.maxDuration || null,
      },
      completionMode: draft.completionMode,
    },
    turnPolicy: {
      maxIterations: intOrNull(draft.maxIterations),
      ...(wakeMessage ? { wakeMessage } : {}),
      allowAgentReschedule: Boolean(draft.allowAgentReschedule),
      postCheckToTranscript: Boolean(draft.postCheckToTranscript),
    },
  };
}

const POLICY_DRAFT_SCALAR_KEYS = [
  "heartbeatEnabled",
  "cadence",
  "heartbeatTimezone",
  "heartbeatTimeOfDay",
  "heartbeatDaysOfWeek",
  "message",
  "noProgressEnabled",
  "noProgressDeadline",
  "debounceMs",
  "cooldownMs",
  "maxTurnsPerHour",
  "skipIfSubjectRunning",
  "stopKind",
  "maxTurns",
  "maxDuration",
  "completionMode",
  "maxIterations",
  "wakeMessage",
  "allowAgentReschedule",
  "postCheckToTranscript",
];

/** Picks policy draft fields from a harness or API payload object. */
export function extractPolicyDraft(source = {}, templateId = "standard_agent") {
  const picked = {};
  for (const key of POLICY_DRAFT_SCALAR_KEYS) {
    if (source[key] !== undefined) {
      picked[key] = source[key];
    }
  }
  if (source.scheduledWakes !== undefined) {
    picked.scheduledWakes = scheduledWakesFromPolicy(source.scheduledWakes);
  }
  return { ...policyDraftFromEffectivePolicy({}, templateId), ...picked };
}
